/**
 * modal.jsx
 * 
 * Modal component for login flow
 * 
 **/ 
import React, { Fragment, useRef, useState } from 'react'
import { RegisterFieldset,
         LoginFieldset,
         ForgotPasswordFieldset,
         OneTimePasswordFieldset } from './fieldsets.jsx'

const MODAL_TYPE = Object.freeze({
  LOGIN: 'LOGIN',
  FORGOT_PASSWORD: 'PASSWORD',
  ONE_TIME_PASSWORD: 'OTP',
  REGISTER: 'REGISTER'
})

/**
 * @function Modal
 * 
 * @param active Boolean
 * @param cancelCallback Callable
 * @param actionCallback Callable
 * @param type MODAL_TYPE
 * 
 * A modal component with labels, etc set by `type`
 *  
 **/
const Modal = ({ actionCallback,
                 active,
                 cancelCallback,
                 error,
                 resetCallback,
                 type }) => {
  const [fieldData, setFieldData] = useState({})

  // Callback to shuttle an Object from fields to the modal
  const handleFieldDataChange = (data) => {
    setFieldData({ ...data })
  }

  // Call the configured action against the current data
  const handleAction = (event) => {
    event?.preventDefault()

    actionCallback({ event, fieldData })
  }

  const handleReset = (event) => {
    event.preventDefault()

    resetCallback({ event, fieldData })
  }

  // Set the labels and message for this modal type 
  let title = ''
  let message = ''
  let actionButtonLabel = ''
  let fieldset

  switch (type) {
    case MODAL_TYPE.LOGIN:
      title = 'Login'
      message = 'Please enter your email address and password for Access O\'Keeffe.'
      actionButtonLabel = 'Login'
      fieldset = <LoginFieldset actionCallback={handleAction}
                                closeModalCallback={cancelCallback}
                                stateChangeCallback={handleFieldDataChange}
                                resetCallback={handleReset}/>
      break

    case MODAL_TYPE.FORGOT_PASSWORD:
      title = 'Request Password Reset'
      message = 'Please enter the email address you used to create an Access O’Keeffe account. We will send you a message with a link to reset your password.'
      actionButtonLabel = 'Reset Password'
      fieldset = <ForgotPasswordFieldset stateChangeCallback={handleFieldDataChange} />
      break

    case MODAL_TYPE.ONE_TIME_PASSWORD:
      title = 'One-Time Code'
      message = 'To verify your email address, please enter the one-time code that was sent to your email.'
      actionButtonLabel = 'Login'
      fieldset = <OneTimePasswordFieldset 
                    stateChangeCallback={handleFieldDataChange} 
                    resetCallback={handleReset}/>
      break

    case MODAL_TYPE.REGISTER:
      title = 'Register'
      message = 'To create and save your own lists of interesting items from the Access O’Keeffe site, register for a free account with your name and email address.'
      actionButtonLabel = 'Register'
      fieldset = <RegisterFieldset stateChangeCallback={handleFieldDataChange} />
      break

    default:
      console.warn(`Got unknown type ${type}`)
  }

  return <div className={`login-chip__modal modal ${active ? 'is-active' : ''} `}>
          <div className="modal-background"></div>
          <div className="modal-card">
            <header className="modal-card-head">
              <h1 className="modal-card-title">
                {title}</h1>
              <button className="btn-icon-only icon-lg btn-close"
                      aria-label="close"
                      onClick={cancelCallback} >
                <span className="icon icon-close-large"></span><span className="sr-only">Close this
                  dialog</span>
              </button>
            </header>
            <section className="modal-card-body min-height-container">
              <div className={`notification notification-danger ${ error ? '' : 'is-hidden' }`}>
                <h2 className="notification-title mb-2">
                  <i role="presentation" aria-hidden="true" className="icon icon-danger icon-login-error icon-lg mr-2"></i>
                  {error}
                </h2>
              </div>
              <div className='mb-2'>
                { message }
              </div>
              { fieldset }
            </section>

            <footer className="modal-card-foot">
              <div className="is-flex ml-auto">
                <button className="button btn-sm" onClick={cancelCallback}>Cancel</button>
                <button className="btn-solid btn-sm" onClick={handleAction}>{ actionButtonLabel }</button>
              </div>
            </footer>
          </div>
        </div>
}

export { Modal, MODAL_TYPE }